/* eslint-disable react/prop-types */
import React, { useContext, useEffect, useMemo } from "react";
import { useSortBy, useTable } from "react-table";
import TableLayout from "../../../../components/orderTable/table-layout";
import { OldOrderDetailsData, OrderDetailsData } from "../../../../contexts";
import { getOrderStatuses } from "../../../../utils/helpers";
import { PRESORTED, PRESORTED_ORDER_TYPE, STRIPS_ORDER_TYPE } from "../../../../utils/constants";
import { useSourcesData } from "../../../../hooks/fetch-hooks/get-sources";
import { Cell, Cell2, CellChannel, CellSource } from "../../../../components/orderTable/table-instance";
import { ORDERS_COLUMN } from "../../../../components/orderTable/Constants";
import { buildExtraPharmacyOrderColumns } from "./columns";

// TableInstance for /v2/pharmacy/orders -- a close copy of
// ../../ops/pages/orders-legacy/table-instance.jsx, the same relationship
// LegacyOrdersTableInstance has to legacy's own orderTable stack. Column set is
// legacy's ORDERS_COLUMN (minus its own "Left" column -- see below), extended with
// this console's own extra columns, then the SAME Status/Channel/Source/Action cells
// legacy and the ops console both use, imported unmodified. Rendering goes through the
// UNMODIFIED TableLayout, so row markup, hover states and click-to-open-sidepanel
// behaviour are all legacy's, byte for byte -- OrderType is "PHARMACY_ORDERS", which
// only changes which toolbar buttons/action buttons COLUMNS_FUCTIONALITIES renders
// (see src/components/orderTable/Constants.js).
//
// No ReOrder/Digitize path here, same as the ops version -- this page is never
// mounted for those OrderTypes.

/** Same floor as the ops console's SelectCell -- an unstyled inline <select> renders
 * ~44px wide, too narrow to read a status/channel/source value without opening it. */
const SelectCell = ({ children }) => <div className="min-w-[180px]">{children}</div>;

/** Left-to-right order for the columns a pharmacist reads most, by react-table column id. */
const LEADING_COLUMN_IDS = ["id", "mapUserPatient.Patient.name", "status", "contact", "startDate", "endDate", "daysLeft", "total", "type"];

/** Stable-sort `columns` so LEADING_COLUMN_IDS come first, in that order. */
function orderColumns(columns) {
    const [first, ...rest] = columns;
    const pinned = first?.Header === "#" ? [first] : [];
    const sortable = pinned.length ? rest : columns;

    const rank = (column) => {
        const index = LEADING_COLUMN_IDS.indexOf(column.id || column.accessor);
        return index === -1 ? LEADING_COLUMN_IDS.length : index;
    };

    const ordered = sortable
        .map((column, index) => ({ column, index }))
        .sort((a, b) => rank(a.column) - rank(b.column) || a.index - b.index)
        .map((entry) => entry.column);

    return [...pinned, ...ordered];
}

/** COLUMNS_FUCTIONALITIES.PHARMACY_ORDERS never includes "Reorder", so Cell's ReOrder
 * button never renders and this is never called -- it exists only because Cell reads
 * OldOrderDetailsData unconditionally (React.useContext runs before the capability
 * check), and destructuring `setOldOrder` off a missing Provider would throw. */
const NO_OP_OLD_ORDER = { oldOrder: null, setOldOrder: () => {} };

function PharmacyOrdersTableInstance({ tableData, changeRightBarOpen, setPageSort, pageSort, handleDeleteRow, refetchOrders, hiddenColumns }) {
    const orderStatuses = getOrderStatuses();
    const { channels, getSourcesForChannels } = useSourcesData();
    const { OrderType } = useContext(OrderDetailsData);

    const [columns, data] = useMemo(() => {
        const statusColumn = {
            Header: "Status",
            accessor: "status",
            Cell: (row) => {
                // A pharmacist's queue spans both Presorted and Strips orders, same as
                // the ops console's "All Orders" view -- pick the status set from the
                // row's own type, matching legacy's ALL_ORDER_TYPE branch.
                const types = row?.cell?.row?.original?.type?.includes(PRESORTED)
                    ? orderStatuses[PRESORTED_ORDER_TYPE]
                    : orderStatuses[STRIPS_ORDER_TYPE];
                return <SelectCell>{Cell2(row, "status", types, refetchOrders)}</SelectCell>;
            },
        };

        // legacy's own "Left" column (Header "EndDate"-adjacent, accessor "daysLeft")
        // is dropped here in favour of this console's own "Days Left" column
        // (columns.jsx), which uses daysLeftUntil -- see that file's comment for why
        // keeping both would just reintroduce the two-different-numbers bug this
        // whole rebuild exists to avoid.
        // disableSortBy across the board: this table's row order is the priority sort
        // (index.jsx), applied client-side to the fetched page, not a backend orderBy
        // -- unlike the ops console's version of this table, nothing here consumes
        // react-table's sortBy state, so leaving columns sortable would show a working
        // -looking header arrow that silently does nothing when clicked.
        const columns = orderColumns([
            ...ORDERS_COLUMN.filter((column) => column.Header !== "Left").map((column) => ({ ...column, disableSortBy: true })),
            ...buildExtraPharmacyOrderColumns(),
            statusColumn,
            {
                Header: "Channel",
                accessor: "channel",
                disableSortBy: true,
                Cell: (row) => <SelectCell>{CellChannel(row, "channel", channels, refetchOrders)}</SelectCell>,
            },
            {
                Header: "Source",
                accessor: "source",
                disableSortBy: true,
                Cell: (row) => {
                    const sources = getSourcesForChannels(row?.row?.original?.channel);
                    const current = row?.row?.original?.source;
                    if (!sources?.length) {
                        return current ? <span>{current}</span> : <span className="text-gray-400 text-sm">Set Channel first</span>;
                    }
                    return <SelectCell>{CellSource(row, "source", sources, refetchOrders)}</SelectCell>;
                },
            },
            {
                Header: "Action",
                accessor: "action",
                Cell: (row) => Cell(row, () => {}, handleDeleteRow, OrderType),
            },
        ]);
        return [columns, tableData];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tableData]);

    const tableInstance = useTable(
        {
            columns,
            data,
            initialState: { sortBy: pageSort, hiddenColumns },
            manualSortBy: true,
            autoResetSortBy: false,
            autoResetExpanded: false,
            autoResetPage: false,
            autoResetHiddenColumns: false,
        },
        useSortBy
    );

    const { setHiddenColumns, state: tableState } = tableInstance;
    useEffect(() => {
        const current = tableState.hiddenColumns || [];
        const next = hiddenColumns || [];
        const same = current.length === next.length && next.every((id) => current.includes(id));
        if (!same) setHiddenColumns(next);
    }, [hiddenColumns, tableState.hiddenColumns, setHiddenColumns]);

    return (
        <OldOrderDetailsData.Provider value={NO_OP_OLD_ORDER}>
            <TableLayout {...tableInstance} changeRightBarOpen={changeRightBarOpen} setPageSort={setPageSort} refetchOrders={refetchOrders} />
        </OldOrderDetailsData.Provider>
    );
}

export default PharmacyOrdersTableInstance;
